// Print Presets Management Panel
// Sprint 5: Presets Sidebar

import React, { useState } from 'react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import { ScrollArea } from '@/app/components/ui/scroll-area';
import { Separator } from '@/app/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/app/components/ui/alert-dialog';
import { Save, Trash2, Star, Settings, RotateCcw } from 'lucide-react';
import type { PrintPreset, PrintConfig } from '@/app/types/print';
import { useLanguage } from '@/app/contexts/LanguageContext';

interface PrintPresetsPanelProps {
  presets: PrintPreset[];
  selectedPresetId: string | null;
  currentConfig: PrintConfig;
  onLoadPreset: (presetId: string) => void;
  onSavePreset: (name: string, description?: string) => void;
  onDeletePreset: (presetId: string) => void;
  onResetDefaults: () => void;
}

export function PrintPresetsPanel({
  presets,
  selectedPresetId,
  currentConfig,
  onLoadPreset,
  onSavePreset,
  onDeletePreset,
  onResetDefaults,
}: PrintPresetsPanelProps) {
  const { t } = useLanguage();
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [presetToDelete, setPresetToDelete] = useState<PrintPreset | null>(null);
  const [newPresetName, setNewPresetName] = useState('');
  const [newPresetDescription, setNewPresetDescription] = useState('');
  
  const builtInPresets = presets.filter((p) => p.isBuiltIn);
  const customPresets = presets.filter((p) => !p.isBuiltIn);
  
  const handleSavePreset = () => {
    if (newPresetName.trim()) {
      onSavePreset(newPresetName.trim(), newPresetDescription.trim() || undefined);
      setNewPresetName('');
      setNewPresetDescription('');
      setShowSaveDialog(false);
    }
  };
  
  const handleDeletePreset = () => {
    if (presetToDelete) {
      onDeletePreset(presetToDelete.id);
      setPresetToDelete(null);
      setShowDeleteDialog(false);
    }
  };
  
  const confirmDelete = (preset: PrintPreset) => {
    setPresetToDelete(preset);
    setShowDeleteDialog(true);
  };
  
  return (
    <>
      <div className="p-4 space-y-4">
        <div>
          <h3 className="font-medium mb-2 flex items-center gap-2">
            <Settings className="w-4 h-4" />
            {t.table.printDialog.presets.title}
          </h3>
          <p className="text-xs text-muted-foreground">
            {t.table.printDialog.presets.desc}
          </p>
        </div>
        
        <div className="flex flex-col gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSaveDialog(true)}
            className="w-full justify-start"
          >
            <Save className="w-4 h-4 mr-2" />
            {t.table.printDialog.presets.saveCurrent}
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={onResetDefaults}
            className="w-full justify-start"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            {t.table.printDialog.presets.reset}
          </Button>
        </div>
        
        <Separator />
        
        <ScrollArea className="h-[calc(85vh-300px)]">
          <div className="space-y-6">
            {/* Built-in Presets */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Star className="w-4 h-4 text-yellow-500" />
                <h4 className="text-sm font-medium">{t.table.printDialog.presets.standardPresets}</h4>
              </div>
              
              <div className="space-y-2">
                {builtInPresets.map((preset) => {
                  const display = t.table.printDialog.presets.items?.[preset.id];
                  const name = display?.name ?? preset.name;
                  const description = display?.desc ?? preset.description;

                  return (
                    <Button
                      key={preset.id}
                      variant={selectedPresetId === preset.id ? 'secondary' : 'ghost'}
                      size="sm"
                      onClick={() => onLoadPreset(preset.id)}
                      className="w-full justify-start text-left h-auto py-3 px-3"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{name}</div>
                        {description && (
                          <div className="text-xs text-muted-foreground mt-1 leading-snug">
                            {description}
                          </div>
                        )}
                      </div>
                    </Button>
                  );
                })}
              </div>
            </div>
            
            {/* Custom Presets */}
            {customPresets.length > 0 && (
              <>
                <Separator />
                
                <div>
                  <h4 className="text-sm font-medium mb-3">{t.table.printDialog.presets.userPresets}</h4>
                  
                  <div className="space-y-2">
                    {customPresets.map((preset) => (
                      <div
                        key={preset.id}
                        className={`flex items-start gap-2 p-2 rounded-md transition-colors ${
                          selectedPresetId === preset.id
                            ? 'bg-secondary'
                            : 'hover:bg-secondary/50'
                        }`}
                      >
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onLoadPreset(preset.id)}
                          className="flex-1 justify-start text-left h-auto py-1 px-2"
                        >
                          <div>
                            <div className="font-medium text-sm">{preset.name}</div>
                            {preset.description && (
                              <div className="text-xs text-muted-foreground mt-0.5">
                                {preset.description}
                              </div>
                            )}
                          </div>
                        </Button>
                        
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => confirmDelete(preset)}
                          className="h-8 w-8 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </ScrollArea>
      </div>
      
      {/* Save Preset Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.table.printDialog.presets.saveTitle}</DialogTitle>
            <DialogDescription>
              {t.table.printDialog.presets.saveDesc}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="presetName">{t.table.printDialog.presets.nameLabel}</Label>
              <Input
                id="presetName"
                value={newPresetName}
                onChange={(e) => setNewPresetName(e.target.value)}
                placeholder={t.table.printDialog.presets.placeholderName}
                autoFocus
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="presetDescription">{t.table.printDialog.presets.descLabel}</Label>
              <Textarea
                id="presetDescription"
                value={newPresetDescription}
                onChange={(e) => setNewPresetDescription(e.target.value)}
                placeholder={t.table.printDialog.presets.placeholderDesc}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveDialog(false)}>
              {t.table.printDialog.actions.cancel}
            </Button>
            <Button onClick={handleSavePreset} disabled={!newPresetName.trim()}>
              {t.table.printDialog.presets.saveBtn}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.table.printDialog.presets.deleteConfirmTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {t.table.printDialog.presets.deleteConfirmDesc.replace('{name}', presetToDelete?.name || '')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t.table.printDialog.actions.cancel}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePreset}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t.table.printDialog.presets.deleteBtn}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}